import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  Typography,
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import api, { API_BASE_URL } from "../../utils/api";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const fileIconFor = (fileType) => {
  if (fileType === "application/pdf") return <PictureAsPdfIcon sx={{ color: "#c0392b" }} />;
  if (fileType?.startsWith("image/")) return <ImageIcon sx={{ color: "#2f63ad" }} />;
  return <InsertDriveFileIcon sx={{ color: "#6b778c" }} />;
};

// Admin-facing document manager for an election — upload files here, voters
// see them inline on the ballot page via DocumentsViewer.js.
const DocumentsTab = ({ election }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const fileInputRef = useRef(null);

  const loadDocuments = async () => {
    if (!election?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/elections/${election.id}/documents`);
      setDocuments(res.data || []);
    } catch (err) {
      console.error("Failed to load election documents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [election?.id]);

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append("files", f));
      await api.post(`/elections/${election.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await loadDocuments();
      setSnack({ open: true, message: `${files.length} document${files.length !== 1 ? "s" : ""} uploaded`, severity: "success" });
    } catch (err) {
      console.error("Failed to upload documents:", err);
      setSnack({ open: true, message: err.response?.data?.message || "Failed to upload documents", severity: "error" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (doc) => {
    try {
      await api.delete(`/elections/${election.id}/documents/${doc.id}`);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (err) {
      console.error("Failed to delete document:", err);
      setSnack({ open: true, message: "Failed to delete document", severity: "error" });
    }
  };

  return (
    <Card sx={{ ...adminCardSx, p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
          Election Documents
        </Typography>
        <Button
          variant="contained"
          component="label"
          startIcon={uploading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <UploadFileIcon />}
          disabled={uploading}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            backgroundColor: "#2f63ad",
            "&:hover": { backgroundColor: "#275592", boxShadow: "none" },
          }}
        >
          {uploading ? "Uploading..." : "Upload"}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            multiple
            onChange={handleFilesSelected}
          />
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
        Attach bylaws, proxy forms, budgets, or other election-related documents. Voters can view
        these directly on the ballot page without leaving it.
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : documents.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#9aa5b4", textAlign: "center", py: 3 }}>
          No documents attached yet.
        </Typography>
      ) : (
        <List sx={{ py: 0 }}>
          {documents.map((doc) => (
            <ListItem
              key={doc.id}
              sx={{ borderBottom: "1px solid rgba(47,99,173,0.06)", px: 0 }}
              secondaryAction={
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <Tooltip title="Open">
                    <IconButton
                      size="small"
                      component="a"
                      href={`${API_BASE_URL}/election-documents/file/${doc.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(doc)}>
                      <DeleteIcon fontSize="small" sx={{ color: "#c0392b" }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{fileIconFor(doc.file_type)}</ListItemIcon>
              <ListItemText
                primary={doc.file_name}
                secondary={doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : null}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((p) => ({ ...p, open: false }))} sx={{ borderRadius: "12px" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default DocumentsTab;
